import React, { useMemo, useEffect, useLayoutEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    flexRender
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import CellValueModal from './CellValueModal';
import { formatCellValue } from '../utils/cellFormat';
import { pinnedLeftOffsets, prunePinned, togglePinned } from '../utils/gridPin';
import { selectionRect, inRect, rangeToTsv, writeClipboard } from '../utils/gridCopy';
import { columnMatches, cellMatches } from '../utils/gridFilter';
import { FiFilter, FiX, FiBookOpen } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { lookupAreas as lookupUserCommentAreas } from '../services/userComments';
import { formatApiError } from '../utils/formatters';
import '../App.css';

import { toCssPx, menuPoint } from '../utils/appZoom';
import { listenDrag, listenRelease } from '../utils/dragListen';

// 사용자 주석 열(2026-10-29, docs/features/32-user-comment.md) — 영역명과 같은 컬럼 바로 오른쪽에 붙는 열의 id 접두어
const UCMT = '__ucmt__';
const ucmtSource = (id) => (String(id).startsWith(UCMT) ? String(id).slice(UCMT.length) : id);
const UCMT_MAX_VALUES = 2000;      // 열 하나에서 한 번에 물을 값 수
const NO_AREAS = [];
const ResultGrid = forwardRef(({ results, columnsMeta, onFetchMoreData, hasMore, isLoadingMore, tabId, loadingMessage, canRetry, hasError, startRowIndex = 0, columnComments = {}, message, isCompactGrid = false, gridFormat, onPasteToEditor, showUserComments = false, connectionId = null }, ref) => {
    const { t } = useTranslation();
    const data = useMemo(() => results && !results.error ? results : [], [results]);
    const tableContainerRef = useRef(null);
    const [columnWidths, setColumnWidths] = useState({});
    const [isResizing, setIsResizing] = useState(false);
    const resizeInfoRef = useRef(null);
    const [modalData, setModalData] = useState(null);
    // 열 고정(머리글 우클릭) · 행 고정(순번 더블클릭) — utils/gridPin.js
    const [pinnedCols, setPinnedCols] = useState([]);
    const [pinnedRows, setPinnedRows] = useState([]);   // data 의 행 번호(0부터), 고정한 순서
    const [headerMenu, setHeaderMenu] = useState(null); // { x, y, key }
    const headerMenuRef = useRef(null);
    const headerRowRef = useRef(null);
    const [headerH, setHeaderH] = useState(0);
    const togglePinRow = useCallback((idx) => setPinnedRows(prev => togglePinned(prev, idx)), []);
    // 열 필터(이름·주석) · 값 필터(보이는 열에서 값이 맞는 행만) — utils/gridFilter.js. 받아 둔 행 안에서만 거른다(2026-10-29)
    const [colQuery, setColQuery] = useState('');
    const [rowQuery, setRowQuery] = useState('');
    const commentOf = useCallback((key) => {
        const upperKey = String(key || '').toUpperCase();
        for (const cols of Object.values(columnComments || {})) {
            if (cols && cols[upperKey]) return cols[upperKey];
        }
        return null;
    }, [columnComments]);
    const commentOfRef = useRef(commentOf);
    commentOfRef.current = commentOf;
    // 사용자 주석 — 결과의 연결에서 받아 둔 영역(리덕스) 중 이름이 같은 컬럼의 머리글에 아이콘을 보이고, 그 아이콘을 누른 컬럼만
    // 오른쪽에 주석 열을 펼친다(2026-10-29 사용자 요청 — 켜기만 해서는 표가 바뀌지 않는다). 값의 주석은 펼칠 때 받고 탭마다 받아 둔다
    const ucmtAreas = useSelector(state => {
        const e = state.userComments && connectionId ? state.userComments.areasByConn[connectionId] : null;
        return e ? e.areas : NO_AREAS;
    });
    const [ucmt, setUcmt] = useState({});          // { 컬럼: { 값: 주석 | '' } }
    const [ucmtVer, setUcmtVer] = useState(0);
    const ucmtRef = useRef(ucmt);
    ucmtRef.current = ucmt;
    const ucmtWarnedRef = useRef(false);
    const [ucmtOpen, setUcmtOpen] = useState([]);  // 주석 열을 펼친 컬럼
    const ucmtMatched = useMemo(() => {
        if (!showUserComments || !ucmtAreas.length) return [];
        const names = new Set(ucmtAreas.map(a => String(a.name).toUpperCase()));
        const keys = (data && data.length > 0) ? Object.keys(data[0]) : (Array.isArray(columnsMeta) ? columnsMeta.map(m => m.name).filter(Boolean) : []);
        return keys.filter(k => names.has(String(k).toUpperCase()));
    }, [showUserComments, ucmtAreas, data, columnsMeta]);
    const ucmtCols = useMemo(() => ucmtMatched.filter(k => ucmtOpen.includes(k)), [ucmtMatched, ucmtOpen]);
    const ucmtColsKey = ucmtCols.join('\n');
    // 머리글은 열 정의(useMemo) 안에서 그려지므로 맞는 컬럼·펼침 여부·토글은 ref 로 읽는다(그릴 때의 값)
    const ucmtMatchedRef = useRef(new Set());
    ucmtMatchedRef.current = new Set(ucmtMatched);
    const ucmtOpenRef = useRef(ucmtOpen);
    ucmtOpenRef.current = ucmtOpen;
    const toggleUcmt = useCallback((k) => setUcmtOpen(prev => (prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k])), []);
    /** 셀의 주석 — 아직 안 물었으면 undefined, 없으면 '' */
    const ucmtText = (k, v) => {
        if (v == null) return '';
        const m = ucmtRef.current[k];
        return m ? m[String(v).trim()] : undefined;
    };
    // 셀 범위 선택 — { a: 앵커, f: 포커스 }, r = 화면 행 번호, c = 데이터 열 번호(순번 열 제외). utils/gridCopy.js
    const [sel, setSel] = useState(null);
    const draggingRef = useRef(false);
    const movedRef = useRef(false);
    // 결과 영역 글자 크기(마우스 휠로 바뀌는 --font-size-grid) — 순번 열 폭을 따라 키우려고 읽는다
    const [gridFontPx, setGridFontPx] = useState(12);
    // 셀 그리기 함수 안에서 쓰는 번역 — 열 정의(useMemo)의 의존성에 t 를 넣으면 t 가 바뀔 때마다
    // 열이 새로 만들어지고, 그것을 보는 폭 계산 effect 가 상태를 새 객체로 갈아 끼워 렌더가 끝없이 돈다.
    const tRef = useRef(t);
    tRef.current = t;
    
    // 데이터 변경 시 자동 스크롤 트리거 (필요한 경우)
    useEffect(() => {
        if (tableContainerRef.current && data.length > 0) {
            // 렌더링 완료 후 크기 확인을 위해 setTimeout 사용
            setTimeout(() => {
                const container = tableContainerRef.current;
                if (container) {
                    // 스크롤이 가능한 상태인데 맨 아래에 있다면 강제로 스크롤 이벤트 발생
                    const scrollPercentage = (container.scrollTop + container.clientHeight) / container.scrollHeight;
                    
                    if (container.scrollHeight > container.clientHeight && scrollPercentage >= 0.95 && hasMore && !isLoadingMore) {
                        container.dispatchEvent(new Event('scroll'));
                    }
                }
            }, 100);
        }
    }, [data.length, tabId, hasMore, isLoadingMore]);

    // Data type settings - 조정 가능한 너비 (최소/최대 제한 완화)
    const typeSettings = {
        number: { default: 150, min: 50, max: 800 },
        date: { default: 150, min: 50, max: 800 },
        boolean: { default: 120, min: 50, max: 800 },
        string: { default: 200, min: 50, max: 800 },
        id: { default: 120, min: 50, max: 800 },
        default: { default: 150, min: 50, max: 800 }
    };

    // Data type detection and width calculation logic
    const detectDataType = useCallback((columnKey, sampleData) => {
        if (!sampleData || sampleData.length === 0) return 'default';
        const samples = sampleData.slice(0, 10).map(row => row[columnKey]).filter(val => val != null);
        if (samples.length === 0) return 'default';
        if (columnKey.toLowerCase().includes('id') || columnKey.toLowerCase().endsWith('_id')) return 'id';
        const isNumber = samples.every(val => !isNaN(val) && !isNaN(parseFloat(val)));
        if (isNumber) return 'number';
        const isBool = samples.every(val => val === true || val === false || val === 'true' || val === 'false' || val === '1' || val === '0' || val === 'Y' || val === 'N');
        if (isBool) return 'boolean';
        const isDate = samples.some(val => {
            const dateStr = String(val);
            return /^\d{4}-\d{2}-\d{2}/.test(dateStr) || /^\d{2}\/\d{2}\/\d{4}/.test(dateStr) || !isNaN(Date.parse(dateStr));
        });
        if (isDate) return 'date';
        return 'string';
    }, []);

    const measureTextWidth = useCallback((text, fontSize = '0.9em') => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        context.font = `${fontSize} Arial`;
        return context.measureText(String(text)).width;
    }, []);

    // 큰 값인지 판단 (100자 이상 또는 객체 타입)
    const isLargeValue = useCallback((value) => {
        if (value == null) return false;
        const strValue = String(value);
        return strValue.length > 100 || typeof value === 'object';
    }, []);

    // 셀 클릭 핸들러 — programmatic large 값이거나 시각적으로 잘려있는 (...) 경우 popup.
    // 시각 잘림 판정: span 또는 부모 td 의 scrollWidth > clientWidth.
    const handleCellClick = useCallback((e, value, columnName) => {
        // 범위를 고르는 중이면(끌기·Shift) 값 팝업을 띄우지 않는다
        if (movedRef.current || (e && (e.shiftKey || e.ctrlKey || e.metaKey))) return;
        const target = e?.currentTarget;
        const td = target?.closest?.('td');
        const visuallyTruncated = !!(
            (target && target.scrollWidth > target.clientWidth + 1) ||
            (td && td.scrollWidth > td.clientWidth + 1)
        );
        if (visuallyTruncated || isLargeValue(value)) {
            setModalData({ value: String(value), columnName });
        }
    }, [isLargeValue]);

    // 셀 더블클릭 — 잘림 여부와 무관하게 항상 에디터 cursor 위치에 값 paste.
    const handleCellDoubleClick = useCallback((e, value) => {
        if (typeof onPasteToEditor !== 'function') return;
        onPasteToEditor(String(value == null ? '' : value));
    }, [onPasteToEditor]);

    // 헤더 더블클릭 — 컬럼명을 에디터 cursor 위치에 삽입 (잘림 무관, 항상 키 자체 사용).
    const handleHeaderDoubleClick = useCallback((columnKey) => {
        if (typeof onPasteToEditor !== 'function') return;
        if (!columnKey) return;
        onPasteToEditor(String(columnKey));
    }, [onPasteToEditor]);

    // 순번 열 폭 — 가장 큰 번호의 자릿수 × 글자 폭(+여백). 기본 크기·세 자리까지는 예전 그대로 30px.
    const rowNumWidth = useMemo(() => {
        const digits = Math.max(2, String(startRowIndex + Math.max(1, data.length)).length);
        return Math.max(30, Math.ceil(digits * gridFontPx * 0.62 + 7));
    }, [startRowIndex, data.length, gridFontPx]);

    const calculateOptimalWidth = useCallback((columnKey, dataType) => {
        // 헤더 + 샘플 값 30개 중 최대 길이 측정 (양 모드 공통).
        const samples = (data || []).slice(0, 30)
            .map(r => (r ? r[columnKey] : null))
            .map(v => formatCellValue(v, gridFormat).text.slice(0, 80));
        let maxLen = String(columnKey || '').length;
        for (const s of samples) if (s.length > maxLen) maxLen = s.length;

        if (isCompactGrid) {
            // 압축 모드: 1글자당 ~6.5px (font ≈ 12px) + padding/여유 20px. min 40, max 300.
            const px = Math.round(maxLen * 6.5) + 20;
            return Math.max(40, Math.min(px, 300));
        }
        // 일반 모드: 압축보다 여유 (font ≈ 13px). 글자가 딱 맞게 잘려 잘림 표시가 자주 뜬다는
        // 지적이 있어 여유를 늘렸다 — 글자당 7.5px + 패딩 32px, 하한 76px, 상한 480px
        // (2026-09-18). 더 좁게 보려면 압축 보기 토글이 있다.
        const px = Math.round(maxLen * 7.5) + 32;
        return Math.max(76, Math.min(px, 480));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCompactGrid, data, gridFormat]);

    const { columns, initialWidths } = useMemo(() => {
        // 0건 결과에서도 헤더를 그리기 위해, data[0] 이 없으면 columnsMeta(백엔드 메타) 사용
        const columnKeys = (data && data.length > 0)
            ? Object.keys(data[0])
            : (Array.isArray(columnsMeta) ? columnsMeta.map(m => m.name).filter(Boolean) : []);
        if (columnKeys.length === 0) return { columns: [], initialWidths: {} };
        const cols = [];
        const widths = {};
        const ucmtSet = new Set(ucmtColsKey ? ucmtColsKey.split('\n') : []);

        // 첫 번째 컬럼으로 로우 순번 추가 (고정 너비, export 제외 대상)
        // 폭은 **자릿수·글자 크기**로 정한다(2026-10-07) — 휠로 글자를 키우면 번호도 커지는데 30px 고정이라 잘렸다
        cols.push({
            id: '__rowNumber__', // 특수 접두사로 시스템 컬럼임을 표시
            header: () => <span>#</span>,
            cell: info => (
                <div
                    title={tRef.current('common:rg.rowPinHint', { n: startRowIndex + info.row.index + 1 })}
                    onDoubleClick={() => togglePinRow(info.row.index)}
                >
                    {startRowIndex + info.row.index + 1}
                </div>
            ),
            meta: {
                dataType: '__system__', // 시스템 컬럼 타입
                isSystemColumn: true, // export 제외용 플래그
                optimalWidth: rowNumWidth,
                settings: { default: rowNumWidth, min: rowNumWidth, max: rowNumWidth } // 고정 너비
            },
            enableSorting: false,
            enableResizing: false, // 크기 조정 비활성화
            size: rowNumWidth, // 고정 크기 설정
            minSize: rowNumWidth,
            maxSize: rowNumWidth
        });
        widths['__rowNumber__'] = rowNumWidth;

        // 기존 데이터 컬럼들 추가
        columnKeys.forEach(key => {
            const dataType = detectDataType(key, data);
            const optimalWidth = calculateOptimalWidth(key, dataType);
            cols.push({
                // **값은 accessorFn 으로 키 그대로 읽는다.** TanStack 은 accessorKey 에 점이 있으면
                // 중첩 경로로 해석한다 — `SELECT 'DROP SYNONYM X.'||NAME||';'` 의 컬럼 이름
                // `'DROPSYNONYMX.'||NAME||';'` 를 row["'DROPSYNONYMX"]["'||NAME||';'"] 로 찾아
                // 값이 있는데도 [NULL] 로 보였다(실측 2026-10-01). id 도 못 박는다 — 안 주면 점을
                // '_' 로 바꿔 만든다. accessorKey 는 아래 코드(폭 조절·내보내기)가 키로 쓰므로 남긴다.
                id: key,
                accessorFn: (row) => row[key],
                accessorKey: key,
                header: () => {
                    // 모든 테이블의 컬럼 주석에서 해당 컬럼명 검색 — **그릴 때의** 주석을 읽는다(ref). 열 정의를 만들 때의 것을
                    // 쓰면 결과가 주석보다 먼저 온 탭에는 끝내 주석이 붙지 않았다(2026-10-29 신고)
                    const comment = commentOfRef.current(key);
                    // 영역명과 같은 컬럼 — 아이콘을 누르면 오른쪽에 주석 열을 펼치고 접는다
                    const ucmtIcon = ucmtMatchedRef.current.has(key) ? (
                        <button
                            type="button"
                            className={`rg-ucmt-toggle${ucmtOpenRef.current.includes(key) ? ' open' : ''}`}
                            title={tRef.current(ucmtOpenRef.current.includes(key) ? 'common:rg.ucmtCollapse' : 'common:rg.ucmtExpand')}
                            aria-label={tRef.current(ucmtOpenRef.current.includes(key) ? 'common:rg.ucmtCollapse' : 'common:rg.ucmtExpand')}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); toggleUcmt(key); }}
                        >
                            <FiBookOpen size={11} />
                        </button>
                    ) : null;
                    // 이름과 아이콘을 한 줄 가운데로 맞춘다(아이콘이 글자 아래로 처지던 것, 2026-11-04)
                    const nameEl = ucmtIcon ? <span className="rg-ucmt-name">{key}{ucmtIcon}</span> : key;
                    return comment ? (
                        <span title={comment}>
                            {nameEl}
                            <span style={{ display: 'block', fontSize: '10px', color: '#888', fontWeight: 'normal', lineHeight: 1.2, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                                {comment}
                            </span>
                        </span>
                    ) : <span>{nameEl}</span>;
                },
                cell: info => {
                    const value = info.getValue();
                    // 표시만 포맷하고 원본(raw)은 보존 — 툴팁·더블클릭 붙여넣기·팝업은 원본을 쓴다.
                    const fmt = formatCellValue(value, gridFormat);
                    const isLarge = isLargeValue(value);
                    const displayValue = isLarge && fmt.text.length > 100
                        ? fmt.text.substring(0, 100) + '...'
                        : fmt.text;

                    return (
                        <span
                            className={fmt.isNull ? 'result-grid-cell is-null' : 'result-grid-cell'}
                            title={fmt.isNull ? '' : fmt.raw}
                            data-type={dataType}
                            onClick={(e) => handleCellClick(e, value, key)}
                            onDoubleClick={(e) => handleCellDoubleClick(e, value)}
                        >
                            {displayValue}
                        </span>
                    );
                },
                meta: { dataType, optimalWidth, settings: typeSettings[dataType] || typeSettings.default }
            });
            widths[key] = optimalWidth;
            if (ucmtSet.has(key)) {
                // 사용자 주석 열 — 값은 그릴 때 받아 둔 주석에서 읽는다(ref). 받으면 표 데이터를 새로 건네 걸러내기 값도 새로 계산된다(tableData)
                const uid = `${UCMT}${key}`;
                const ucw = isCompactGrid ? 140 : 180;
                cols.push({
                    id: uid,
                    accessorKey: uid,
                    accessorFn: (row) => ucmtText(key, row[key]) || null,
                    header: () => (
                        <span className="rg-ucmt-head" title={tRef.current('common:rg.ucmtHeadHint', { col: key })}>
                            {tRef.current('common:rg.ucmtHead')}
                            <span className="rg-ucmt-head-src">{key}</span>
                        </span>
                    ),
                    cell: info => {
                        const txt = ucmtText(key, info.row.original[key]);
                        if (txt === undefined) return <span className="result-grid-cell rg-ucmt-cell rg-ucmt-pending">…</span>;
                        return <span className="result-grid-cell rg-ucmt-cell" title={txt || ''}>{txt}</span>;
                    },
                    meta: { dataType: 'string', isUserComment: true, sourceKey: key, optimalWidth: ucw, settings: typeSettings.string },
                });
                widths[uid] = ucw;
            }
        });
        return { columns: cols, initialWidths: widths };
        // ucmtText 는 ref 를 읽는다 — 주석이 올 때마다 열을 다시 만들지 않게 일부러 뺀다
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, columnsMeta, detectDataType, calculateOptimalWidth, startRowIndex, isLargeValue, handleCellClick, handleCellDoubleClick, gridFormat, togglePinRow, rowNumWidth, ucmtColsKey, isCompactGrid]);

    // 결과가 바뀌면(다른 결과 탭) 행 고정은 풀고, 없어진 열의 고정은 버린다.
    // 더 불러오기(같은 탭에 행이 붙는 것)는 행 번호가 그대로라 행 고정을 유지한다.
    useEffect(() => { setPinnedRows([]); setHeaderMenu(null); setColQuery(''); setRowQuery(''); }, [tabId]);
    useEffect(() => { setUcmt({}); setUcmtVer(0); setUcmtOpen([]); ucmtWarnedRef.current = false; }, [tabId, connectionId]);

    // 사용자 주석 — 아직 안 물은 값만 모아 묻는다(더 불러온 행의 새 값도). 주석 조회 쿼리는 서버에서 한 번 돈다
    useEffect(() => {
        if (!ucmtColsKey || !connectionId) return undefined;
        const want = {};
        let any = false;
        for (const k of ucmtColsKey.split('\n')) {
            const have = ucmtRef.current[k] || {};
            const vs = new Set();
            for (const row of data) {
                const v = row ? row[k] : null;
                if (v == null) continue;
                const sv = String(v).trim();
                if (!sv || Object.prototype.hasOwnProperty.call(have, sv)) continue;
                vs.add(sv);
                if (vs.size >= UCMT_MAX_VALUES) break;
            }
            if (vs.size) { want[k] = [...vs]; any = true; }
        }
        if (!any) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            let got = {};
            try {
                const r = await lookupUserCommentAreas(connectionId, want);
                if (cancelled) return;
                got = r.comments || {};
                if (r.errors && r.errors.length && !ucmtWarnedRef.current) {
                    ucmtWarnedRef.current = true;
                    toast.warn(tRef.current('common:rg.ucmtError', { message: r.errors[0].error }));
                }
            } catch (err) {
                if (cancelled) return;
                if (!ucmtWarnedRef.current) { ucmtWarnedRef.current = true; toast.error(formatApiError(err, tRef.current)); }
            }
            // 못 찾은 값도 '' 로 적어 둔다 — 다시 묻지 않게
            setUcmt(prev => {
                const next = { ...prev };
                for (const [k, vs] of Object.entries(want)) {
                    const m = { ...(next[k] || {}) };
                    const g = got[k] || {};
                    vs.forEach(v => { m[v] = g[v] != null ? String(g[v]) : ''; });
                    next[k] = m;
                }
                return next;
            });
            setUcmtVer(n => n + 1);
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [ucmtColsKey, data, connectionId]);
    useEffect(() => {
        setPinnedCols(prev => prunePinned(prev, columns.map(c => c.id)));
    }, [columns]);

    // 머리글 우클릭 메뉴 — 바깥을 누르거나 Esc 면 닫는다
    useEffect(() => {
        if (!headerMenu) return undefined;
        const onDown = (e) => { if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) setHeaderMenu(null); };
        const onKey = (e) => { if (e.key === 'Escape') setHeaderMenu(null); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [headerMenu]);

    useEffect(() => {
        if (Object.keys(initialWidths).length > 0) {
            setColumnWidths(prevWidths => {
                const newWidths = { ...prevWidths };
                Object.keys(initialWidths).forEach(key => {
                    if (!newWidths[key]) newWidths[key] = initialWidths[key];
                });
                // 순번 열은 사용자가 못 바꾸는 계산값 — 늘 새 값으로
                if (initialWidths.__rowNumber__) newWidths.__rowNumber__ = initialWidths.__rowNumber__;
                return newWidths;
            });
        }
    }, [initialWidths]);

    // 압축 모드 toggle 시 사용자가 수동 리사이즈했던 columnWidths 도 초기화 — 새 모드의
    // initialWidths 가 적용되도록. 토글 안 한 일반 렌더에서는 사용자 수동 폭 보존.
    const prevCompactRef = useRef(isCompactGrid);
    useEffect(() => {
        if (prevCompactRef.current !== isCompactGrid) {
            prevCompactRef.current = isCompactGrid;
            setColumnWidths(initialWidths);
        }
    }, [isCompactGrid, initialWidths]);

    // 모든 컬럼 width 의 합 — wrapper 의 minWidth 로 적용해 부모(.result-grid)가 가로 overflow 인지하게.
    // table 의 width:0 + table-layout:fixed 만으론 outer width 가 0 으로 보고돼 부모 overflow:auto 가
    // 작동 안 함. JS 로 계산한 값을 인라인 style 로 wrapper 에 강제하면 CSS quirks 와 무관하게 정확.
    // 열 필터로 숨긴 열은 빼고 더한다 — 안 빼면 숨긴 만큼 오른쪽에 빈 칸이 남는다
    const totalColumnsWidth = useMemo(() => {
        const keys = Object.keys(initialWidths);
        if (keys.length === 0) return 0;
        const filterCols = colQuery.trim();
        let sum = 0;
        for (const k of keys) {
            if (filterCols && k !== '__rowNumber__' && !columnMatches(ucmtSource(k), commentOf(ucmtSource(k)), filterCols)) continue;
            sum += (columnWidths[k] || initialWidths[k] || 0);
        }
        return sum;
    }, [columnWidths, initialWidths, colQuery, commentOf]);

    // Column resizing handlers
    const handleResizeMove = useCallback((e) => {
        if (!resizeInfoRef.current) return;
        const { columnKey, startX, startWidth, settings, isSystemColumn } = resizeInfoRef.current;
        
        // 시스템 컬럼(로우 순번)은 리사이징 불가
        if (isSystemColumn) return;
        
        const deltaX = toCssPx(e.clientX - startX);   // 화면 확대 중이면 화면 px → CSS px
        const newWidth = Math.min(Math.max(startWidth + deltaX, settings.min), settings.max);
        setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
    }, []);

    const handleResizeEnd = useCallback(() => {
        setIsResizing(false);
        resizeInfoRef.current = null;
    }, []);

    const handleResizeStart = useCallback((e, columnKey, columnId) => {
        e.preventDefault();
        
        // 시스템 컬럼(로우 순번)은 리사이징 불가
        const columnDef = columns.find(c => (c.accessorKey === columnKey) || (c.id === columnId));
        if (columnDef?.meta?.isSystemColumn) {
            return; // 리사이징 시작하지 않음
        }
        
        setIsResizing(true);
        resizeInfoRef.current = {
            columnKey: columnKey || columnId,
            startX: e.clientX,
            startWidth: columnWidths[columnKey] || (columnDef?.meta?.optimalWidth) || 120,
            settings: columnDef?.meta?.settings || typeSettings.default,
        };
        // 분리 창에서도 그 창의 문서로 받는다(utils/dragListen) — 본창 document 에 걸면 놓아도 끝나지 않았다
        listenDrag(e, handleResizeMove, handleResizeEnd);
    }, [columnWidths, columns, handleResizeMove, handleResizeEnd]);

    // Throttled Infinite Scroll Logic
    const throttleTimeout = useRef(null);
    const lastFetchCall = useRef(null); // 마지막 fetch 호출 추적

    const throttledScrollHandler = useCallback((e) => {
        if (throttleTimeout.current) return; // If a timeout is already set, do nothing

        throttleTimeout.current = setTimeout(() => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            
            // fetch 함수가 없으면 중단
            if (!onFetchMoreData) {
                throttleTimeout.current = null;
                return;
            }
            
            // 로딩 중이면 중단
            if (isLoadingMore) {
                throttleTimeout.current = null;
                return;
            }
            
            // 정상 상태에서 더 이상 데이터가 없으면 중단
            if (!hasMore && !hasError) {
                throttleTimeout.current = null;
                return;
            }
            
            // 에러 상태인데 재시도할 수 없으면 중단
            if (hasError && !canRetry) {
                throttleTimeout.current = null;
                return;
            }
            
            const threshold = scrollHeight * 0.95;
            
            if (scrollTop + clientHeight >= threshold && clientHeight < scrollHeight) {
                // 중복 호출 방지: 같은 tabId로 이미 호출 중인지 확인
                const currentTime = Date.now();
                const currentCall = `${tabId}___${currentTime}`; // 고유한 구분자 사용
                
                // lastFetchCall이 있고, 같은 tabId이며, 5초 이내라면 중복으로 판단
                if (lastFetchCall.current) {
                    const lastCallParts = lastFetchCall.current.split('___');
                    if (lastCallParts.length === 2) {
                        const [lastTabId, lastTimeStr] = lastCallParts;
                        const lastTime = parseInt(lastTimeStr);
                        if (lastTabId === tabId && (currentTime - lastTime) < 5000) {
                            throttleTimeout.current = null;
                            return;
                        }
                    }
                }
                
                lastFetchCall.current = currentCall;
                onFetchMoreData(tabId);
            }
            throttleTimeout.current = null; // Clear timeout after execution
        }, 200); // Throttle for 200ms

    }, [hasMore, isLoadingMore, onFetchMoreData, tabId, hasError, canRetry]);

    useEffect(() => {
        const containerElement = tableContainerRef.current;
        if (containerElement) {
            containerElement.addEventListener('scroll', throttledScrollHandler);
            return () => {
                containerElement.removeEventListener('scroll', throttledScrollHandler);
                if (throttleTimeout.current) {
                    clearTimeout(throttleTimeout.current);
                    throttleTimeout.current = null;
                }
                // lastFetchCall 초기화
                lastFetchCall.current = null;
            };
        }
    }, [throttledScrollHandler]);

    // 열 필터 — 이름이나 주석에 글이 들어간 열만 보인다(쉼표로 여럿). 순번 열은 늘 보인다
    const columnVisibility = useMemo(() => {
        const vis = {};
        if (!colQuery.trim()) return vis;
        for (const c of columns) {
            if (c.meta && c.meta.isSystemColumn) continue;
            // 사용자 주석 열은 원래 컬럼을 따라간다
            const src = ucmtSource(c.id);
            if (!columnMatches(src, commentOf(src), colQuery)) vis[c.id] = false;
        }
        return vis;
    }, [columns, colQuery, commentOf]);
    const visibleDataIds = useMemo(
        () => columns.filter(c => !(c.meta && c.meta.isSystemColumn) && columnVisibility[c.id] !== false).map(c => c.id),
        [columns, columnVisibility],
    );

    // 주석을 받으면 같은 행들을 새 배열로 건넨다 — 표가 행의 값(걸러내기에 쓰는 주석 열 값)을 새로 계산한다
    const tableData = useMemo(() => (ucmtVer ? data.slice() : data), [data, ucmtVer]);
    const table = useReactTable({
        data: tableData,
        columns,
        state: { columnVisibility, globalFilter: rowQuery.trim() },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        // 값 필터는 **보이는 데이터 열**에서만 본다(열 필터가 걸려 있으면 그 열들에서만).
        // 기본 판정은 첫 행 값의 형(문자·숫자)으로 열을 고르는데, 첫 행이 NULL 인 열이 빠져 직접 정한다
        getColumnCanGlobalFilter: (column) => !(column.columnDef.meta && column.columnDef.meta.isSystemColumn) && column.getIsVisible(),
        globalFilterFn: (row, columnId, filterValue) => cellMatches(row.getValue(columnId), filterValue, gridFormat),
    });

    // 고정 열의 왼쪽 위치 · 고정 행이 설 높이(머리글 아래)
    const widthOf = useCallback((k) => columnWidths[k] || initialWidths[k] || 120, [columnWidths, initialWidths]);
    // 제자리 고정 — 순서는 그대로, 보이는 순서대로 앞의 고정 열 너비만큼 띄워 세운다(utils/gridPin)
    const { left: pinLeft, last: lastPinned } = useMemo(
        () => pinnedLeftOffsets(visibleDataIds, pinnedCols, widthOf, rowNumWidth),
        [visibleDataIds, pinnedCols, widthOf, rowNumWidth],
    );
    // 머리글 높이는 주석 줄·압축 보기·폭(줄바꿈)에 따라 달라진다 — 고정 행을 그 바로 아래에 세운다
    useLayoutEffect(() => {
        const h = headerRowRef.current ? headerRowRef.current.offsetHeight : 0;
        if (h !== headerH) setHeaderH(h);
    }, [headerH, columns, columnWidths, isCompactGrid, pinnedRows.length]);
    /** 셀 하나의 고정 열 모양 — 고정이 아니면 너비만 */
    const pinCell = (columnKey, width, extraStyle) => {
        const left = columnKey != null ? pinLeft[columnKey] : undefined;
        const style = { width: `${width}px`, ...(extraStyle || {}) };
        if (left == null) return { style, className: '' };
        return {
            style: { ...style, left: `${left}px` },
            className: columnKey === lastPinned ? 'rg-pin-col rg-pin-col-last' : 'rg-pin-col',
        };
    };

    // 가상 스크롤 — 화면 밖 행은 DOM 에 두지 않음. 687 행 × 100+ 컬럼 같은 큰 결과셋도 부드럽게.
    const { rows: tableRows } = table.getRowModel();
    // 고정 행 번호는 원래 행 번호다 — 값 필터가 걸리면 tableRows 의 자리가 달라지므로 거르기 전 행에서 찾는다
    const coreRows = table.getCoreRowModel().rows;
    const filtering = !!(colQuery.trim() || rowQuery.trim());
    const rowVirtualizer = useVirtualizer({
        count: tableRows.length,
        getScrollElement: () => tableContainerRef.current,
        // compact: padding 1*2 + line(~14) ≈ 18 / normal: padding 3*2 + line(~14) + border 2 ≈ 22
        estimateSize: () => (isCompactGrid ? 18 : 22),
        overscan: 10, // 화면 위/아래로 미리 그릴 여유 행
    });
    const virtualRows = rowVirtualizer.getVirtualItems();
    const pinnedRowSet = useMemo(() => new Set(pinnedRows), [pinnedRows]);
    // 고정 행이 여럿일 때 서로 겹치지 않게 쌓는 간격 — 가상 스크롤이 가정한 행 높이와 같다
    const rowHeight = isCompactGrid ? 18 : 22;
    const totalSize = rowVirtualizer.getTotalSize();
    // 가상 행 위/아래에 빈 공간을 padding tr 로 채워 실제 스크롤 높이 유지.
    const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
    const paddingBottom = virtualRows.length > 0
        ? totalSize - virtualRows[virtualRows.length - 1].end
        : 0;

    // 잘린 셀 표시 — 현재 화면에 보이는 td 들을 측정해서 overflow 면 'truncated' class 부여.
    // virtualizer 덕분에 ~30~50개만 측정 → 비용 안전. 폭/데이터/스크롤 변할 때마다 재계산.
    const firstVirtIndex = virtualRows[0]?.index ?? -1;
    const lastVirtIndex = virtualRows[virtualRows.length - 1]?.index ?? -1;
    useLayoutEffect(() => {
        if (!tableContainerRef.current) return;
        const tds = tableContainerRef.current.querySelectorAll('tbody td');
        tds.forEach(td => {
            // padding tr 의 td 는 colSpan 설정되어 있으니 건너뜀
            if (td.getAttribute('colspan')) {
                td.classList.remove('truncated');
                return;
            }
            const overflow = td.scrollWidth > td.clientWidth + 1;
            td.classList.toggle('truncated', overflow);
        });
    }, [data, columnWidths, isCompactGrid, firstVirtIndex, lastVirtIndex, tableRows.length, sel]);

    // 글자 크기 읽기 — 매 렌더 비교만 하고 바뀔 때만 상태를 고친다(휠 확대/축소는 부모 렌더로 들어온다)
    useLayoutEffect(() => {
        const el = tableContainerRef.current;
        if (!el) return;
        const v = parseFloat(getComputedStyle(el).getPropertyValue('--font-size-grid'));
        if (v && Math.abs(v - gridFontPx) > 0.01) setGridFontPx(v);
    // 의존성을 일부러 비운다 — CSS 변수는 React 가 모르는 값이라 매 렌더 읽어 비교한다(같으면 상태를 안 바꾼다)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    // ── 셀 범위 선택 · 복사 ────────────────────────────────────────────
    // 선택·복사는 **보이는** 열 기준(열 필터로 숨긴 열은 화면 번호에서 빠진다)
    const dataColIds = visibleDataIds;
    const dataColCount = useMemo(() => columns.filter(c => !(c.meta && c.meta.isSystemColumn)).length, [columns]);
    const colIndexOf = useMemo(() => new Map(dataColIds.map((id, i) => [id, i])), [dataColIds]);
    const selRect = selectionRect(sel);
    useEffect(() => { setSel(null); }, [tabId]);
    useEffect(() => {
        const up = () => { draggingRef.current = false; };
        document.addEventListener('mouseup', up);
        return () => document.removeEventListener('mouseup', up);
    }, []);
    const focusGrid = () => { const el = tableContainerRef.current; if (el && document.activeElement !== el) el.focus({ preventScroll: true }); };
    const handleCellMouseDown = (e, r, c) => {
        if (e.button !== 0) return;
        movedRef.current = false;
        if (e.shiftKey && sel) {
            e.preventDefault();
            setSel({ a: sel.a, f: { r, c } });
        } else {
            setSel({ a: { r, c }, f: { r, c } });
        }
        draggingRef.current = true;
        // 놓기는 누른 창의 문서에서 받는다 — 분리 창에서 놓아도 본창은 몰라 선택이 계속 늘어났다
        listenRelease(e, () => { draggingRef.current = false; });
        focusGrid();
    };
    const handleCellMouseEnter = (r, c) => {
        if (!draggingRef.current) return;
        movedRef.current = true;
        window.getSelection && window.getSelection().removeAllRanges();
        setSel(s => (s ? { a: s.a, f: { r, c } } : s));
    };
    const handleRowNumMouseDown = (e, r) => {
        if (e.button !== 0 || dataColIds.length === 0) return;
        e.preventDefault();
        const last = dataColIds.length - 1;
        setSel(prev => (e.shiftKey && prev ? { a: { r: prev.a.r, c: 0 }, f: { r, c: last } } : { a: { r, c: 0 }, f: { r, c: last } }));
        focusGrid();
    };
    const copySelection = useCallback(async (withHeader) => {
        const rect = selectionRect(sel);
        if (!rect) return;
        // 사용자 주석 열은 행에 없는 값이다 — 복사할 때만 붙여 준다
        const rowsData = ucmtColsKey
            ? tableRows.map(rw => {
                const o = { ...rw.original };
                ucmtColsKey.split('\n').forEach(k => { o[`${UCMT}${k}`] = ucmtText(k, rw.original[k]) || null; });
                return o;
            })
            : tableRows.map(rw => rw.original);
        const text = rangeToTsv(rowsData, dataColIds, rect, (v) => {
            const fmt = formatCellValue(v, gridFormat);
            return fmt.isNull ? '' : String(fmt.raw != null ? fmt.raw : fmt.text);
        }, { withHeader });
        const ok = await writeClipboard(text);
        if (ok) toast.success(`${t('common:rg.copied', { rows: rect.r1 - rect.r0 + 1, cols: rect.c1 - rect.c0 + 1 })} ${t('common:rg.copyHint')}`, { autoClose: 2500 });
        else toast.error(t('common:rg.copyFailed'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sel, tableRows, dataColIds, gridFormat, t, ucmtColsKey, ucmtVer]);
    const handleGridKeyDown = (e) => {
        const mod = e.ctrlKey || e.metaKey;
        if (mod && (e.key === 'c' || e.key === 'C')) {
            if (!sel) return;
            e.preventDefault();
            copySelection(e.shiftKey);
        } else if (mod && (e.key === 'a' || e.key === 'A')) {
            if (!tableRows.length || !dataColIds.length) return;
            e.preventDefault();
            setSel({ a: { r: 0, c: 0 }, f: { r: tableRows.length - 1, c: dataColIds.length - 1 } });
        } else if (e.key === 'Escape' && sel) {
            setSel(null);
        }
    };

    // Export용 유틸리티 함수 - 시스템 컬럼 제외한 데이터만 반환
    const getExportableData = useCallback(() => {
        if (!data || data.length === 0) return { columns: [], rows: [] };
        
        // 시스템 컬럼이 아닌 실제 데이터 컬럼만 필터링
        // 사용자 주석 열은 결과가 아니다 — 내보내지 않는다
        const exportColumns = columns.filter(col => !col.meta?.isSystemColumn && !col.meta?.isUserComment);
        const exportColumnKeys = exportColumns.map(col => col.accessorKey).filter(Boolean);
        
        return {
            columns: exportColumnKeys,
            rows: data.map(row => {
                const exportRow = {};
                exportColumnKeys.forEach(key => {
                    exportRow[key] = row[key];
                });
                return exportRow;
            })
        };
    }, [data, columns]);

    // 부모 컴포넌트에서 export 함수에 접근할 수 있도록 설정
    useImperativeHandle(ref, () => ({
        getExportableData
    }), [getExportableData]);

    if (results && results.error) {
        return <div className="error-message">{results.error}</div>;
    }

    // 컬럼 정보(데이터 또는 메타) 가 전혀 없는 경우에만 플레이스홀더 표시
    // — 0건이어도 columnsMeta 가 있으면 아래에서 헤더만 있는 빈 그리드를 렌더 (DBeaver 스타일)
    if ((!data || data.length === 0) && (!Array.isArray(columnsMeta) || columnsMeta.length === 0)) {
        const isEmptyResult = Array.isArray(results) && results.length === 0;
        
        let emptyText = t('common:rg.waiting');
        if (isEmptyResult) {
            // message prop이 있다면 DDL 실행 결과(예: "Table created.", "Query executed.")일 수 있으므로 이를 표시
            if (message && message.trim() && message !== t('common:rg.zeroRows')) {
                emptyText = message;
            } else {
                emptyText = t('common:rg.noRows');
            }
        }
        
        return (
            <div className="result-grid-empty">
                {emptyText}
            </div>
        );
    }

    return (
        <>
            <div className={`rg-filter-bar${filtering ? ' rg-filter-on' : ''}`}>
                <FiFilter size={12} className="rg-filter-icon" aria-hidden="true" />
                <input
                    type="text" className="rg-filter-input" value={colQuery}
                    placeholder={t('common:rg.filterColPlaceholder')} aria-label={t('common:rg.filterCol')}
                    title={t('common:rg.filterColHint')}
                    onChange={(e) => { setColQuery(e.target.value); setSel(null); }}
                />
                <input
                    type="text" className="rg-filter-input" value={rowQuery}
                    placeholder={t('common:rg.filterRowPlaceholder')} aria-label={t('common:rg.filterRow')}
                    title={t('common:rg.filterRowHint')}
                    onChange={(e) => { setRowQuery(e.target.value); setSel(null); }}
                />
                {showUserComments && (
                    <span className="rg-ucmt-status" title={t('common:rg.ucmtStatusHint')}>
                        {ucmtMatched.length ? t('common:rg.ucmtStatusOn', { n: ucmtMatched.length }) : t('common:rg.ucmtStatusNone')}
                    </span>
                )}
                {filtering && (
                    <>
                        <span className="rg-filter-count">
                            {t('common:rg.filterCount', { cols: visibleDataIds.length, colsAll: dataColCount, rows: tableRows.length, rowsAll: data.length })}
                            {hasMore ? ` · ${t('common:rg.filterLoadedOnly')}` : ''}
                        </span>
                        <button
                            type="button" className="rg-filter-clear"
                            onClick={() => { setColQuery(''); setRowQuery(''); setSel(null); }}
                            title={t('common:rg.filterClear')} aria-label={t('common:rg.filterClear')}
                        >
                            <FiX size={12} />
                        </button>
                    </>
                )}
            </div>
            <div
                className={`result-grid${isCompactGrid ? ' result-grid-compact' : ''}`}
                ref={tableContainerRef}
                tabIndex={0}
                onKeyDown={handleGridKeyDown}
                style={{ '--rg-rownum-w': `${rowNumWidth}px` }}
            >
                {/* wrapper — JS 계산 컬럼 합계를 minWidth 로 강제. 부모 overflow:auto 가 이 값을 보고
                    가로 스크롤바 결정. table 자체의 outer width(0) 와 무관하게 정확한 폭 보장. */}
                <div
                    className="result-grid-table-wrap"
                    style={totalColumnsWidth > 0
                        ? { width: `${totalColumnsWidth}px`, minWidth: `${totalColumnsWidth}px` }
                        : undefined}
                >
                <table>
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id} ref={headerRowRef}>
                                {headerGroup.headers.map((header) => {
                                    const columnKey = header.column.columnDef.accessorKey;
                                    const isSystem = !!header.column.columnDef.meta?.isSystemColumn;
                                    const isUcmt = !!header.column.columnDef.meta?.isUserComment;
                                    const width = columnWidths[columnKey] || initialWidths[columnKey] || 120;
                                    const pc = pinCell(isSystem ? null : columnKey, width);
                                    return (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            style={pc.style}
                                            className={[pc.className, isUcmt ? 'rg-ucmt-th' : ''].filter(Boolean).join(' ') || undefined}
                                            onDoubleClick={!isSystem && !isUcmt && columnKey ? () => handleHeaderDoubleClick(columnKey) : undefined}
                                            onContextMenu={(e) => {
                                                if (isSystem || !columnKey) return;
                                                e.preventDefault();
                                                setHeaderMenu({ ...menuPoint(e, 200, 140, e.view || window), key: columnKey });
                                            }}
                                            title={!isSystem && !isUcmt && columnKey ? t('common:rg.headerHint') : undefined}
                                        >
                                            <div className="column-header-content">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                            </div>
                                            <div
                                                className="column-resizer"
                                                onMouseDown={(e) => handleResizeStart(e, header.column.columnDef.accessorKey, header.column.columnDef.id)}
                                            />
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                        {pinnedRows.map((idx, i) => {
                            const row = coreRows[idx];
                            if (!row) return null;
                            const isLast = i === pinnedRows.length - 1;
                            return (
                                <tr key={`pin-${row.id}`} className={`rg-pinned-row${isLast ? ' rg-pinned-row-last' : ''}`}>
                                    {row.getVisibleCells().map(cell => {
                                        const columnKey = cell.column.columnDef.accessorKey;
                                        const isSystem = !!cell.column.columnDef.meta?.isSystemColumn;
                                        const width = columnWidths[columnKey] || initialWidths[columnKey] || 120;
                                        // 머리글 아래에 붙인다 — 고정 행이 여럿이면 차례로 아래로
                                        const pc = pinCell(isSystem ? null : columnKey, width, { top: `${headerH + i * rowHeight}px` });
                                        return (
                                            <td key={`pin-${cell.id}`} data-type={cell.column.columnDef.meta?.dataType} style={pc.style} className={pc.className || undefined}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </thead>
                    <tbody>
                        {paddingTop > 0 && (
                            <tr aria-hidden="true"><td colSpan={columns.length} style={{ height: `${paddingTop}px`, padding: 0, border: 'none' }} /></tr>
                        )}
                        {virtualRows.map(virtualRow => {
                            const row = tableRows[virtualRow.index];
                            if (!row) return null;
                            return (
                                <tr
                                    key={row.id}
                                    data-index={virtualRow.index}
                                    // 5행마다 살짝 다른 톤(사용자 요청 2026-10-07) — 번호 기준이라 스크롤·더 불러오기에도 띠가 흔들리지 않는다
                                    className={[
                                        pinnedRowSet.has(row.index) ? 'rg-row-pinned' : '',
                                        Math.floor((startRowIndex + row.index) / 5) % 2 === 1 ? 'rg-band' : '',
                                    ].filter(Boolean).join(' ') || undefined}
                                >
                                    {row.getVisibleCells().map(cell => {
                                        const columnKey = cell.column.columnDef.accessorKey;
                                        const isSystem = !!cell.column.columnDef.meta?.isSystemColumn;
                                        const width = columnWidths[columnKey] || initialWidths[columnKey] || 120;
                                        const pc = pinCell(isSystem ? null : columnKey, width);
                                        const ci = isSystem ? -1 : colIndexOf.get(cell.column.id);
                                        const selected = !isSystem && inRect(selRect, virtualRow.index, ci);
                                        const cls = [pc.className, selected ? 'rg-sel' : ''].filter(Boolean).join(' ');
                                        return (
                                            <td
                                                key={cell.id}
                                                data-type={cell.column.columnDef.meta?.dataType}
                                                style={pc.style}
                                                className={cls || undefined}
                                                onMouseDown={isSystem
                                                    ? (e) => handleRowNumMouseDown(e, virtualRow.index)
                                                    : (e) => handleCellMouseDown(e, virtualRow.index, ci)}
                                                onMouseEnter={isSystem ? undefined : () => handleCellMouseEnter(virtualRow.index, ci)}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        {paddingBottom > 0 && (
                            <tr aria-hidden="true"><td colSpan={columns.length} style={{ height: `${paddingBottom}px`, padding: 0, border: 'none' }} /></tr>
                        )}
                    </tbody>
                </table>
                </div>
                {isLoadingMore && (
                    <div className="loading-more-indicator">
                        <div className="loading-spinner"></div>
                        <span>{loadingMessage || t('common:rg.loadingMore')}</span>
                    </div>
                )}
                {isResizing && <div className="resize-overlay" />}
            </div>
            {headerMenu && (
                <div ref={headerMenuRef} className="rg-header-menu" style={{ left: headerMenu.x, top: headerMenu.y }} role="menu">
                    <button type="button" role="menuitem" onClick={() => {
                        setPinnedCols(prev => togglePinned(prev, headerMenu.key));
                        setHeaderMenu(null);
                    }}>
                        {pinnedCols.includes(headerMenu.key) ? t('common:rg.unpinCol') : t('common:rg.pinCol')}
                    </button>
                    <button type="button" role="menuitem" disabled={pinnedCols.length === 0} onClick={() => { setPinnedCols([]); setHeaderMenu(null); }}>
                        {t('common:rg.unpinAllCols')}
                    </button>
                    <button type="button" role="menuitem" disabled={pinnedRows.length === 0} onClick={() => { setPinnedRows([]); setHeaderMenu(null); }}>
                        {t('common:rg.unpinAllRows')}
                    </button>
                </div>
            )}
            {modalData && (
                <CellValueModal
                    value={modalData.value}
                    columnName={modalData.columnName}
                    onClose={() => setModalData(null)}
                />
            )}
        </>
    );
});

// 컴포넌트 display name 설정 (디버깅용)
ResultGrid.displayName = 'ResultGrid';

export default ResultGrid;
